import { NgClass, NgFor } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ZopDrawerComponent } from '@zopup/zop-ux-core';
import { Subject, takeUntil } from 'rxjs';
import {
  Scheme,
  Theme,
  Themes,
  ZopUxLayoutLibConfig,
} from '../../../services/config/config.types';
import { ZopUxLayoutConfigService } from '../../../services/config/config.service';

@Component({
    selector: 'zop-settings',
    templateUrl: './settings.component.html',
    styles: [
        `
      zop-settings {
        position: static;
        display: block;
        flex: none;
        width: auto;
      }

      @media (screen and min-width: 1280px) {
        empty-layout + settings .settings-cog {
          right: 0 !important;
        }
      }
    `,
    ],
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatIconModule,
        ZopDrawerComponent,
        MatButtonModule,
        NgFor,
        NgClass,
        MatTooltipModule,
    ]
})
export class SettingsComponent implements OnInit, OnDestroy {
  config: ZopUxLayoutLibConfig;
  layout: string;
  scheme: 'dark' | 'light';
  theme: string;
  themes: Themes;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private _zopConfigService: ZopUxLayoutConfigService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to config changes
    this._zopConfigService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: ZopUxLayoutLibConfig) => {
        // Store the config
        this.config = config;
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Set the layout on the config
   *
   * @param layout
   */
  setLayout(layout: string): void {
    // Clear the 'layout' query param to allow layout changes
    this._router
      .navigate([], {
        queryParams: {
          layout: null,
        },
        queryParamsHandling: 'merge',
      })
      .then(() => {
        // Set the config
        this.config.layout = layout;
        this._zopConfigService.config = this.config;
      });
  }

  /**
   * Set the scheme on the config
   *
   * @param scheme
   */
  setScheme(scheme: Scheme): void {
    this.config.scheme = scheme;
    this._zopConfigService.config = this.config;
  }

  /**
   * Set the theme on the config
   *
   * @param theme
   */
  setTheme(theme: Theme): void {
    this.config.theme = theme;
    this._zopConfigService.config = this.config;
  }
}
